import { MessageDescriptor } from '@lingui/core';
import { defineMessage } from '@lingui/core/macro';
import {
    AbcClassificationLabel,
    ArrayContainsOp,
    AssemblyDTO,
    AvailabilityType,
    CategorySpecificationTypeEnum,
    ComplianceStatus,
    ConflictMineralStatus,
    CustomPartCategoryDetails,
    CustomPartCategoryOriginEnum,
    CustomPartOfferDeliveryMethod,
    CustomPartTypeEnum,
    DepanelizationDTO,
    DifferenceStatus,
    EmailEventStatus,
    EqualityOp,
    GenericPartTypes,
    InventoryPreferenceType,
    ItemClass,
    LeadTimeUnit,
    NoLongerSelectableReason,
    OfferedPartStatus,
    OfferTcoCostType,
    OperatorField,
    OperatorId,
    OverallRisk,
    PackageMountingEnum,
    Packaging,
    PackagingWithUnknown,
    PartAlternativeSimilarityEnum,
    PartAlternativeStrategy,
    PartIdTypes,
    PriceType,
    QuantityType,
    QuantityUnit,
    QuoteRequestLineItemApprovalStatus,
    QuoteRequestLineItemAssignmentReason,
    QuoteRequestLineItemStatus,
    QuoteRequestStatus,
    RfqStatus,
    RuleBasedSelectionFallback,
    RuleBasedSelectionPartSpecification,
    SnapshotTagType,
    SolutionRemovalReason,
    SolutionTag,
    SourcingPreferenceOptions,
    SourcingTemplateTags,
    SupplierLineCardItemSourceDTO,
    SupplierOrigin,
    SupplierPartType,
    SupplierPreference,
    SupplierTag,
    SupplierType,
    TotalCostOfOwnershipRuleCategory,
    TotalCostOfOwnershipRuleConditionType,
} from '@luminovo/api-contracts';
import { transEnum } from '@luminovo/frontend-utils';

import { formatCustomPartCategoryName } from './formatters/formatCustomPartCategoryName';
import { QuoteRequestFieldCategory, QuoteRequestFieldConfig, SolutionStatus } from './types';

export const priceTypeTranslations: Record<PriceType, MessageDescriptor> = {
    [PriceType.QuotePrice]: defineMessage({ message: 'Quote price' }),
    [PriceType.ListPrice]: defineMessage({ message: 'List price' }),
    [PriceType.ContractPrice]: defineMessage({ message: 'Contract price' }),
    [PriceType.CustomerNegotiatedPrice]: defineMessage({
        message: 'Customer negotiated price',
    }),
    [PriceType.PurchasePrice]: defineMessage({ message: 'Purchase price' }),
    [PriceType.StandardPrice]: defineMessage({ message: 'Standard price' }),
    [PriceType.TargetPrice]: defineMessage({ message: 'Target price' }),
};

export const itemClassTranslations: Record<ItemClass, MessageDescriptor> = {
    [ItemClass.Standard]: defineMessage({ message: 'Standard' }),
    [ItemClass.NonStandard]: defineMessage({ message: 'Non-standard' }),
};

export const customPartOfferDeliveryMethodTranslations: Record<CustomPartOfferDeliveryMethod, MessageDescriptor> = {
    [CustomPartOfferDeliveryMethod.AirFreight]: defineMessage({ message: 'Air freight' }),
    [CustomPartOfferDeliveryMethod.SeaFreight]: defineMessage({ message: 'Sea freight' }),
};

export const offeredPartStatusTranslations: Record<OfferedPartStatus, MessageDescriptor> = {
    [OfferedPartStatus.Matching]: defineMessage({ message: 'Matching' }),
    [OfferedPartStatus.PartAlternative]: defineMessage({ message: 'Part alternative' }),
};

export const packagingTranslations: Record<Packaging, MessageDescriptor> = {
    [Packaging.AmmoPack]: defineMessage({ message: 'Ammo Pack' }),
    [Packaging.Bag]: defineMessage({ message: 'Bag' }),
    [Packaging.Bulk]: defineMessage({ message: 'Bulk' }),
    [Packaging.Reel]: defineMessage({ message: 'Reel' }),
    [Packaging.ReReel]: defineMessage({ message: 'Re-Reel' }),
    [Packaging.Spool]: defineMessage({ message: 'Spool' }),
    [Packaging.Tape]: defineMessage({ message: 'Tape' }),
    [Packaging.Tray]: defineMessage({ message: 'Tray' }),
    [Packaging.Tube]: defineMessage({ message: 'Tube' }),
    [Packaging.Carton]: defineMessage({ message: 'Carton' }),
};

export const packagingWithUnknownTranslations: Record<PackagingWithUnknown, MessageDescriptor> = {
    [PackagingWithUnknown.AmmoPack]: defineMessage({ message: 'Ammo Pack' }),
    [PackagingWithUnknown.Bag]: defineMessage({ message: 'Bag' }),
    [PackagingWithUnknown.Bulk]: defineMessage({ message: 'Bulk' }),
    [PackagingWithUnknown.Reel]: defineMessage({ message: 'Reel' }),
    [PackagingWithUnknown.ReReel]: defineMessage({ message: 'Re-Reel' }),
    [PackagingWithUnknown.Spool]: defineMessage({ message: 'Spool' }),
    [PackagingWithUnknown.Tape]: defineMessage({ message: 'Tape' }),
    [PackagingWithUnknown.Tray]: defineMessage({ message: 'Tray' }),
    [PackagingWithUnknown.Tube]: defineMessage({ message: 'Tube' }),
    [PackagingWithUnknown.Carton]: defineMessage({ message: 'Carton' }),
    [PackagingWithUnknown.Unknown]: defineMessage({ message: 'Unknown' }),
};

export const dayFormatTranslations: Record<string, MessageDescriptor> = {
    weekDay: defineMessage({ message: 'Week days' }),
    businessDay: defineMessage({ message: 'Business days' }),
    calendarDay: defineMessage({ message: 'Calendar days' }),
};

export const solutionStatusTranslations: Record<SolutionStatus, MessageDescriptor> = {
    [SolutionStatus.Good]: defineMessage({ message: 'Good' }),
    [SolutionStatus.Warning]: defineMessage({ message: 'Warning' }),
    [SolutionStatus.Error]: defineMessage({ message: 'Error' }),
};

export const supplierPreferenceTranslations: Record<SupplierPreference, MessageDescriptor> = {
    [SupplierPreference.NotApproved]: defineMessage({
        message: 'Non-approved supplier',
    }),
    [SupplierPreference.Approved]: defineMessage({
        message: 'Approved supplier',
    }),
    [SupplierPreference.Preferred]: defineMessage({
        message: 'Preferred supplier',
    }),
    [SupplierPreference.Excluded]: defineMessage({
        message: 'Excluded supplier',
    }),
};

export const supplierTagTranslations: Record<SupplierTag, MessageDescriptor> = {
    [SupplierTag.QuotePartner]: defineMessage({ message: 'Quote partner' }),
    [SupplierTag.ManuallyAdded]: defineMessage({ message: 'Manually added' }),
    [SupplierTag.OffTheShelfPartSupplier]: defineMessage({
        message: 'Off-the-shelf part supplier',
    }),
    [SupplierTag.PcbSupplier]: defineMessage({ message: 'PCB supplier' }),
    [SupplierTag.CustomPartSupplier]: defineMessage({
        message: 'Custom part supplier',
    }),
    [SupplierTag.AssemblySupplier]: defineMessage({
        message: 'Assembly supplier',
    }),
    [SupplierTag.Distributor]: defineMessage({ message: 'Distributor' }),
    [SupplierTag.Manufacturer]: defineMessage({ message: 'Manufacturer' }),
    [SupplierTag.Ems]: defineMessage({ message: 'EMS' }),
    [SupplierTag.System]: defineMessage({ message: 'System' }),
    [SupplierTag.Erp]: defineMessage({ message: 'ERP' }),
    [SupplierTag.Manual]: defineMessage({ message: 'Manual' }),
};

export const supplierTypeTranslations: Record<SupplierType, MessageDescriptor> = {
    [SupplierType.Distributor]: defineMessage({ message: 'Distributor' }),
    [SupplierType.Manufacturer]: defineMessage({ message: 'Manufacturer' }),
    [SupplierType.Ems]: defineMessage({ message: 'EMS' }),
};

export const supplierPartTypeTranslations: Record<SupplierPartType, MessageDescriptor> = {
    [SupplierPartType.OffTheShelf]: defineMessage({
        message: 'Off-the-shelf part',
    }),
    [SupplierPartType.Pcb]: defineMessage({ message: 'PCB' }),
    [SupplierPartType.Custom]: defineMessage({ message: 'Custom part' }),
    [SupplierPartType.Assembly]: defineMessage({ message: 'Assembly' }),
};

export const supplierOriginTranslations: Record<SupplierOrigin, MessageDescriptor> = {
    [SupplierOrigin.System]: defineMessage({ message: 'System' }),
    [SupplierOrigin.Erp]: defineMessage({ message: 'ERP' }),
    [SupplierOrigin.Manual]: defineMessage({ message: 'Manual' }),
};

export const lineCardSourceTranslations: Record<SupplierLineCardItemSourceDTO['type'], MessageDescriptor> = {
    System: defineMessage({ message: 'System' }),
    Tenant: defineMessage({ message: 'Manual' }),
};
export const solutionTagTranslations: Record<SolutionTag, MessageDescriptor> = {
    [SolutionTag.Customer]: defineMessage({ message: 'Customer price' }),
    [SolutionTag.RfQ]: defineMessage({ message: 'Project price' }),
    [SolutionTag.Negotiation]: defineMessage({ message: 'Negotiation price' }),
    [SolutionTag.ManualOffer]: defineMessage({ message: 'Manual' }),
    [SolutionTag.SupplierNotPreferred]: defineMessage({
        message: 'Supplier not preferred',
    }),
    [SolutionTag.SupplierNotApproved]: defineMessage({
        message: 'Supplier not approved',
    }),
    [SolutionTag.SupplierPreferred]: defineMessage({
        message: 'Preferred supplier',
    }),
    [SolutionTag.SupplierApproved]: defineMessage({
        message: 'Approved supplier',
    }),
    [SolutionTag.SupplierExcluded]: defineMessage({
        message: 'Excluded supplier',
    }),
    [SolutionTag.QuotePrice]: defineMessage({ message: 'Quote price' }),
    [SolutionTag.ContractPrice]: defineMessage({ message: 'Contract price' }),
    [SolutionTag.ListPrice]: defineMessage({ message: 'List price' }),
    [SolutionTag.CustomerNegotiatedPrice]: defineMessage({
        message: 'Customer negotiated price',
    }),
    [SolutionTag.PurchasePrice]: defineMessage({ message: 'Purchase price' }),
    [SolutionTag.StandardPrice]: defineMessage({ message: 'Standard price' }),
    [SolutionTag.Expiring]: defineMessage({ message: 'Expiring' }),
    [SolutionTag.Expired]: defineMessage({ message: 'Expired' }),
    [SolutionTag.CreationDate]: defineMessage({ message: 'Creation date' }),
    [SolutionTag.LongLeadTime]: defineMessage({ message: 'Long lead time' }),
    [SolutionTag.Old]: defineMessage({ message: 'Old' }),
    [SolutionTag.Outdated]: defineMessage({ message: 'Outdated' }),
    // Deprecated: no longer emitted by the backend (replaced by UnknownLeadTime). Kept for snapshot parsing.
    [SolutionTag.Unavailable]: defineMessage({ message: 'Unavailable' }),
    [SolutionTag.UnknownLeadTime]: defineMessage({
        message: 'Unknown lead time',
    }),
    [SolutionTag.OnOrder]: defineMessage({ message: 'On order' }),
    [SolutionTag.UnitMismatch]: defineMessage({
        message: 'Unit of measurement mismatch',
    }),
    [SolutionTag.OnePoint]: defineMessage({ message: 'Estimated' }),
    [SolutionTag.Extrapolated]: defineMessage({ message: 'Estimated' }),
    [SolutionTag.Interpolated]: defineMessage({ message: 'Estimated' }),
    [SolutionTag.LowStock]: defineMessage({ message: 'Low stock' }),
    [SolutionTag.Excess]: defineMessage({ message: 'Excess material' }),
    [SolutionTag.AutoSelected]: defineMessage({ message: 'AutoSelected' }),
    [SolutionTag.Restricted]: defineMessage({ message: 'Restricted' }),
    [SolutionTag.Selected]: defineMessage({ message: 'Manually selected' }),
    [SolutionTag.OutdatedManual]: defineMessage({
        message: 'Outdated manually selected solution',
    }),
    [SolutionTag.Converted]: defineMessage({ message: 'Conversion rate' }),
    [SolutionTag.Inventory]: defineMessage({ message: 'Inventory' }),
    [SolutionTag.ExternalSupplier]: defineMessage({ message: 'Market' }),
    [SolutionTag.Packaging]: defineMessage({ message: 'Packaging' }),
    [SolutionTag.OneTimeCost]: defineMessage({ message: 'One-time costs' }),
    [SolutionTag.ManualOneTimeCost]: defineMessage({
        message: 'Manual one-time costs',
    }),
    [SolutionTag.AggregatedQuantity]: defineMessage({
        message: 'Aggregated quantity',
    }),
    [SolutionTag.ScrapQuantity]: defineMessage({ message: 'Scrap quantity' }),
    [SolutionTag.Consigned]: defineMessage({ message: 'Consigned' }),
    [SolutionTag.ThirdPartyOrigin]: defineMessage({
        message: 'Third party origin',
    }),
    [SolutionTag.ExceedsOfferedQuantity]: defineMessage({
        message: 'Exceeds offered quantity',
    }),
    [SolutionTag.GreatlyExceedsOfferedQuantity]: defineMessage({
        message: 'Greatly exceeds offered quantity',
    }),
    [SolutionTag.NoApprovedParts]: defineMessage({
        message: 'No approved parts',
    }),
    [SolutionTag.ManualStatus]: defineMessage({ message: 'Manual status' }),
    [SolutionTag.InvalidSpecification]: defineMessage({
        message: 'Outdated specification',
    }),
    [SolutionTag.NonCancellableNonReturnable]: defineMessage({ message: 'NCNR' }),
    [SolutionTag.SourcingScenario]: defineMessage({
        message: 'Sourcing scenario',
    }),
    [SolutionTag.ShippingTime]: defineMessage({ message: 'Shipping time' }),
    [SolutionTag.PanelMismatch]: defineMessage({ message: 'Panel mismatch' }),
    [SolutionTag.ReferencePrice]: defineMessage({
        message: 'Reference price',
    }),
    [SolutionTag.ManufacturerPreferred]: defineMessage({
        message: 'Manufacturer preferred',
    }),
    [SolutionTag.OnlyValidAsStableSelection]: defineMessage({
        message: 'Newer version available',
    }),
    [SolutionTag.NoLongerSelectable]: defineMessage({
        message: 'No longer selectable',
    }),
    [SolutionTag.ExcludedPriceType]: defineMessage({
        message: 'Excluded price type',
    }),
    [SolutionTag.OfferValidForWindow]: defineMessage({
        message: 'Valid for validation window',
    }),
    [SolutionTag.OfferCreatedInWindow]: defineMessage({
        message: 'Created within validation window',
    }),
    [SolutionTag.OfferMissingValidFromInfo]: defineMessage({
        message: 'Missing valid from date',
    }),
    [SolutionTag.OfferMissingValidFromWarn]: defineMessage({
        message: 'Missing valid from date',
    }),
    [SolutionTag.OfferMissingValidUntilInfo]: defineMessage({
        message: 'Missing valid until date',
    }),
    [SolutionTag.OfferMissingValidUntilWarn]: defineMessage({
        message: 'Missing valid until date',
    }),
    [SolutionTag.OfferPartiallyValidForWindow]: defineMessage({
        message: 'Partially overlaps validation window',
    }),
    [SolutionTag.OfferInvalidForWindow]: defineMessage({
        message: 'Outside validation window',
    }),
    [SolutionTag.BadOfferValidRange]: defineMessage({
        message: 'Invalid validity date range',
    }),
    [SolutionTag.ApprovedPartOption]: defineMessage({ message: 'Approved part' }),
    [SolutionTag.PendingPartOption]: defineMessage({ message: 'Pending part' }),
    [SolutionTag.RejectedPartOption]: defineMessage({ message: 'Rejected part' }),
    [SolutionTag.PartOptionMissing]: defineMessage({
        message: 'Part missing from BOM item',
    }),
    [SolutionTag.PreviouslyRestricted]: defineMessage({
        message: 'Previously restricted',
    }),
    [SolutionTag.NoOffers]: defineMessage({ message: 'No offers' }),
    [SolutionTag.Pending]: defineMessage({ message: 'Pending' }),
    [SolutionTag.ExcludedFromSourcing]: defineMessage({
        message: 'Excluded from sourcing',
    }),
    [SolutionTag.PartAlternative]: defineMessage({
        message: 'Alternative',
    }),
    [SolutionTag.PartAlternativeWarning]: defineMessage({
        message: 'Alternative',
    }),
};

export const noLongerSelectableReasonTranslations: Record<NoLongerSelectableReason['type'], MessageDescriptor> = {
    NewerVersionAvailable: defineMessage({
        message: 'A newer version of this offer is available',
    }),
    RemovedByOfferDataSources: defineMessage({
        message: "The offer's data source is excluded from this sourcing scenario",
    }),
    // Never formatted directly, the nested `SolutionRemovalReason` is shown instead.
    RemovedBySolutionFilters: defineMessage({
        message: 'The offer is excluded from this sourcing scenario',
    }),
};

export const solutionRemovalReasonTranslations: Record<SolutionRemovalReason, MessageDescriptor> = {
    NotEnoughQuantity: defineMessage({
        message: 'The offer no longer covers the required quantity',
    }),
    NotClosestPricePoint: defineMessage({
        message: 'Another price break is a closer match for the required quantity',
    }),
    Excess: defineMessage({
        message: 'The excess this solution would create is no longer accepted',
    }),
    ExcludedSupplier: defineMessage({
        message: 'The supplier is excluded from this sourcing scenario',
    }),
    ExcludedPriceType: defineMessage({
        message: 'The price type is excluded from this sourcing scenario',
    }),
    OutsideValidityWindow: defineMessage({
        message: 'The offer is outside the validity window',
    }),
    OtherSourcingScenario: defineMessage({
        message: 'The offer is restricted to a different sourcing scenario',
    }),
    MismatchedPricePoint: defineMessage({
        message: 'The price break does not match the required quantity',
    }),
};

export const inventoryPreferenceTranslations: Record<InventoryPreferenceType, MessageDescriptor> = {
    PreferInventory: defineMessage({ message: 'Prefer inventory' }),
    Neutral: defineMessage({ message: 'Neutral' }),
    PreferMarketOffers: defineMessage({ message: 'Prefer market offers' }),
};

export const partAlternativeStrategyTranslations: Record<PartAlternativeStrategy, MessageDescriptor> = {
    IncludePartAlternativesWithoutWarning: defineMessage({ message: 'Include' }),
    IncludePartAlternativesAsWarning: defineMessage({ message: 'Include with warning' }),
    ExcludePartAlternatives: defineMessage({ message: 'Exclude' }),
};

export const operatorIdTranslations: Record<OperatorId, MessageDescriptor> = {
    Eq: defineMessage({ message: '=' }),
    Gt: defineMessage({ message: '>' }),
    Gte: defineMessage({ message: '≥' }),
    Lt: defineMessage({ message: '<' }),
    Lte: defineMessage({ message: '≤' }),
    // InRange: defineMessage({ message: '' }),
    And: defineMessage({ message: 'and' }),
    Or: defineMessage({ message: 'or' }),
    In: defineMessage({ message: '=' }),
};

export const comparisonOperatorIdTranslations: Record<
    Exclude<OperatorId, 'Or' | 'And' | 'InRange'>,
    MessageDescriptor
> = {
    Eq: defineMessage({ message: '=' }),
    Gt: defineMessage({ message: '>' }),
    Gte: defineMessage({ message: '≥' }),
    Lt: defineMessage({ message: '<' }),
    Lte: defineMessage({ message: '≤' }),
    In: defineMessage({ message: '=' }),
};

export const conditionOperatorIdTranslations: Record<'Or' | 'And', MessageDescriptor> = {
    Or: defineMessage({ message: 'or' }),
    And: defineMessage({ message: 'and' }),
};

export const operatorFieldTranslations: Record<OperatorField, MessageDescriptor> = {
    PartType: defineMessage({ message: 'Part type' }),
    PartCategory: defineMessage({ message: 'Part category' }),
    AggregatedQuantity: defineMessage({
        message: 'Aggregated quantity of the part',
    }),
    OrderSize: defineMessage({ message: 'Order size of the assembly' }),
    Packaging: defineMessage({ message: 'Packaging' }),
    UnitPrice: defineMessage({ message: 'Unit price' }),
    Package: defineMessage({ message: 'Package' }),
    Mounting: defineMessage({ message: 'Mounting' }),
};

export const availabilityTypeTranslations: Record<AvailabilityType, MessageDescriptor> = {
    [AvailabilityType.LeadTime]: defineMessage({ message: 'Lead time' }),
    [AvailabilityType.OnOrder]: defineMessage({ message: 'On order' }),
    [AvailabilityType.OnOrderWithUnknownLeadTime]: defineMessage({
        message: 'On order, unknown lead time',
    }),
    [AvailabilityType.Stock]: defineMessage({ message: 'In stock' }),
};

export const rfqStatusTranslations: Record<RfqStatus, MessageDescriptor> = {
    [RfqStatus.RequestInDraft]: defineMessage({ message: 'Project in draft' }),
    [RfqStatus.QuotationInProgress]: defineMessage({
        message: 'Quotation in progress',
    }),
    [RfqStatus.QuotationAvailable]: defineMessage({
        message: 'Quotation available',
    }),
    [RfqStatus.NoQuotation]: defineMessage({ message: 'No quotation' }),
    [RfqStatus.NoOrder]: defineMessage({ message: 'No order' }),
    [RfqStatus.OrderPlaced]: defineMessage({ message: 'Order placed' }),
    [RfqStatus.OrderConfirmed]: defineMessage({ message: 'Order confirmed' }),
    [RfqStatus.OrderInProduction]: defineMessage({
        message: 'Order in production',
    }),
    [RfqStatus.OrderShipped]: defineMessage({ message: 'Order shipped' }),
    [RfqStatus.OrderInProcurement]: defineMessage({
        message: 'Order in procurement',
    }),
    [RfqStatus.PaymentFailed]: defineMessage({ message: 'Payment failed' }),
};

export const sourcingPreferenceTranslations: Record<SourcingPreferenceOptions, MessageDescriptor> = {
    [SourcingPreferenceOptions.Fastest]: defineMessage({ message: 'Fastest' }),
    [SourcingPreferenceOptions.BestPrice]: defineMessage({
        message: 'Best price',
    }),
    [SourcingPreferenceOptions.BestPriceBy]: defineMessage({
        message: 'Best price by',
    }),
};

export const partSpecificationTypeTranslations: Record<GenericPartTypes, MessageDescriptor> = {
    [GenericPartTypes.Resistor]: defineMessage({ message: 'Resistor' }),
    [GenericPartTypes.Capacitor]: defineMessage({ message: 'Capacitor' }),
};

export const customPartSpecificationTypeTranslations: Record<CustomPartTypeEnum, MessageDescriptor> = {
    [CustomPartTypeEnum.PCB]: defineMessage({ message: 'PCB' }),
    [CustomPartTypeEnum.Mechanical]: defineMessage({ message: 'Mechanical' }),
    [CustomPartTypeEnum.Cable]: defineMessage({ message: 'Cable & wiring' }),
    [CustomPartTypeEnum.Label]: defineMessage({ message: 'Label & marking' }),
    [CustomPartTypeEnum.Plastic]: defineMessage({ message: 'Plastic' }),
    [CustomPartTypeEnum.Packaging]: defineMessage({ message: 'Packaging' }),
    [CustomPartTypeEnum.Other]: defineMessage({ message: 'Other' }),
};

export const customPartCategorySpecificationTypeTranslations: Record<CategorySpecificationTypeEnum, MessageDescriptor> =
    {
        [CategorySpecificationTypeEnum.Custom]: defineMessage({ message: 'Custom' }),
    };

export function getTranslatedCustomPartCategoryName(category: CustomPartCategoryDetails): string {
    switch (category.type) {
        case 'PCB':
        case 'Other':
            return formatCustomPartCategoryName(category.data.user_category);
        case 'Old':
            return transEnum(category.data.part_type.name, customPartSpecificationTypeTranslations);
    }
}

export const customPartCategoryOriginTranslations: Record<CustomPartCategoryOriginEnum, MessageDescriptor> = {
    [CustomPartCategoryOriginEnum.System]: defineMessage({ message: 'Default' }),
    [CustomPartCategoryOriginEnum.User]: defineMessage({ message: 'User' }),
    [CustomPartCategoryOriginEnum.Import]: defineMessage({ message: 'Import' }),
};

export const packageMountingEnumTranslations: Record<PackageMountingEnum, MessageDescriptor> = {
    [PackageMountingEnum.SMT]: defineMessage({ message: 'SMT' }),
    [PackageMountingEnum.THT]: defineMessage({ message: 'THT' }),
    [PackageMountingEnum.PressFit]: defineMessage({ message: 'Press fit' }),
    [PackageMountingEnum.Other]: defineMessage({ message: 'Other' }),
};

export const partTypesTranslations: Record<PartIdTypes, MessageDescriptor> = {
    [PartIdTypes.Custom]: defineMessage({ message: 'Custom' }),
    [PartIdTypes.CustomComponent]: defineMessage({ message: 'Custom component' }),
    [PartIdTypes.Generic]: defineMessage({ message: 'Generic' }),
    [PartIdTypes.OffTheShelf]: defineMessage({ message: 'OTS' }),
    [PartIdTypes.Ipn]: defineMessage({ message: 'IPN' }),
    [PartIdTypes.RawSpecification]: defineMessage({
        message: 'Raw specification',
    }),
};

/** Includes legacy `Kg` (`QuantityUnitOld`) for forms still using the old weight base unit string. */
export const QuantityUnitRecordSingular: Record<QuantityUnit, MessageDescriptor> & {
    Kg: MessageDescriptor;
} = {
    Pieces: defineMessage({ message: 'Piece' }),
    Kilograms: defineMessage({ message: 'Kilogram' }),
    Grams: defineMessage({ message: 'Gram' }),
    Milligrams: defineMessage({ message: 'Milligram' }),
    Pounds: defineMessage({ message: 'Pound' }),
    Meters: defineMessage({ message: 'Meter' }),
    Centimeters: defineMessage({ message: 'Centimeter' }),
    Millimeters: defineMessage({ message: 'Millimeter' }),
    Feet: defineMessage({ message: 'Foot' }),
    Inches: defineMessage({ message: 'Inch' }),
    Liters: defineMessage({ message: 'Liter' }),
    Centiliters: defineMessage({ message: 'Centiliter' }),
    Milliliters: defineMessage({ message: 'Milliliter' }),
    UsFluidOunces: defineMessage({ message: 'US fl oz' }),
    UsGallons: defineMessage({ message: 'US gallon' }),
    UsPints: defineMessage({ message: 'US pint' }),
    Kg: defineMessage({ message: 'Kilogram' }),
};

export const QuantityUnitRecordPlural: Record<QuantityUnit, MessageDescriptor> & {
    Kg: MessageDescriptor;
} = {
    Pieces: defineMessage({ message: 'Pieces' }),
    Kilograms: defineMessage({ message: 'Kilograms' }),
    Grams: defineMessage({ message: 'Grams' }),
    Milligrams: defineMessage({ message: 'Milligrams' }),
    Pounds: defineMessage({ message: 'Pounds' }),
    Meters: defineMessage({ message: 'Meters' }),
    Centimeters: defineMessage({ message: 'Centimeters' }),
    Millimeters: defineMessage({ message: 'Millimeters' }),
    Feet: defineMessage({ message: 'Feet' }),
    Inches: defineMessage({ message: 'Inches' }),
    Liters: defineMessage({ message: 'Liters' }),
    Centiliters: defineMessage({ message: 'Centiliters' }),
    Milliliters: defineMessage({ message: 'Milliliters' }),
    UsFluidOunces: defineMessage({ message: 'US fl oz' }),
    UsGallons: defineMessage({ message: 'US gallons' }),
    UsPints: defineMessage({ message: 'US pints' }),
    Kg: defineMessage({ message: 'Kilograms' }),
};

export const QuantityTypeRecord: Record<QuantityType, MessageDescriptor> = {
    CardinalNumber: defineMessage({ message: 'Count' }),
    Weight: defineMessage({ message: 'Weight' }),
    Length: defineMessage({ message: 'Length' }),
    Volume: defineMessage({ message: 'Volume' }),
};

export const depanelizationTranslations: Record<DepanelizationDTO, MessageDescriptor> = {
    Milling: defineMessage({ message: 'Milling' }),
    VCut: defineMessage({ message: 'V-cut' }),
    MillingAndVCut: defineMessage({ message: 'Milling and V-cut' }),
};

export const snapshotTagTranslations: Record<SnapshotTagType, MessageDescriptor> = {
    [SnapshotTagType.Live]: defineMessage({ message: 'Live version' }),
    [SnapshotTagType.Manual]: defineMessage({ message: 'Manual' }),
    [SnapshotTagType.Note]: defineMessage({ message: 'Note' }),
    [SnapshotTagType.RfqCreated]: defineMessage({ message: 'Project created' }),
    [SnapshotTagType.RfqStatusChanged]: defineMessage({
        message: 'Project status changed',
    }),
    [SnapshotTagType.SourcingScenarioName]: defineMessage({
        message: 'Sourcing scenario',
    }),
    [SnapshotTagType.SourcingScenarioId]: defineMessage({
        message: 'Sourcing scenario',
    }),
    [SnapshotTagType.CostedBomExported]: defineMessage({
        message: 'Costed BOM exported',
    }),
    [SnapshotTagType.MetadataTooLarge]: defineMessage({
        message: 'Metadata too large',
    }),
};

export const totalCostOfOwnershipRuleCategoryTranslations: Record<TotalCostOfOwnershipRuleCategory, MessageDescriptor> =
    {
        [TotalCostOfOwnershipRuleCategory.Packaging]: defineMessage({
            message: 'Packaging',
        }),
        [TotalCostOfOwnershipRuleCategory.Discount]: defineMessage({
            message: 'Discount',
        }),
        [TotalCostOfOwnershipRuleCategory.Customs]: defineMessage({
            message: 'Customs',
        }),
        [TotalCostOfOwnershipRuleCategory.Shipping]: defineMessage({
            message: 'Shipping',
        }),
        [TotalCostOfOwnershipRuleCategory.Other]: defineMessage({ message: 'Other' }),
    };

export const totalCostOfOwnershipRuleConditionTypeTranslations: Record<
    TotalCostOfOwnershipRuleConditionType,
    MessageDescriptor
> = {
    [TotalCostOfOwnershipRuleConditionType.Packaging]: defineMessage({
        message: 'Packaging',
    }),
    [TotalCostOfOwnershipRuleConditionType.Supplier]: defineMessage({
        message: 'Supplier',
    }),
    [TotalCostOfOwnershipRuleConditionType.CountryOfOrigin]: defineMessage({
        message: 'Country of origin',
    }),
    [TotalCostOfOwnershipRuleConditionType.CustomsCode]: defineMessage({
        message: 'Customs code',
    }),
    [TotalCostOfOwnershipRuleConditionType.Incoterms]: defineMessage({
        message: 'Incoterms',
    }),
    [TotalCostOfOwnershipRuleConditionType.Manufacturer]: defineMessage({
        message: 'Manufacturer',
    }),
};

export const quoteRequestStatusTranslations: Record<QuoteRequestStatus, MessageDescriptor> = {
    [QuoteRequestStatus.NotSent]: defineMessage({ message: 'Draft' }),
    [QuoteRequestStatus.Sent]: defineMessage({ message: 'Sent' }),
    [QuoteRequestStatus.Overdue]: defineMessage({ message: 'Overdue' }),
    [QuoteRequestStatus.Discarded]: defineMessage({ message: 'Discarded' }),
    [QuoteRequestStatus.Received]: defineMessage({ message: 'Received' }),
};

export const quoteRequestLineItemStatusTranslations: Record<QuoteRequestLineItemStatus, MessageDescriptor> = {
    [QuoteRequestLineItemStatus.UnsolicitedOffer]: defineMessage({ message: 'Unsolicited offer' }),
    [QuoteRequestLineItemStatus.NotSent]: defineMessage({ message: 'Not sent' }),
    [QuoteRequestLineItemStatus.Sent]: defineMessage({ message: 'Sent' }),
    [QuoteRequestLineItemStatus.Overdue]: defineMessage({ message: 'Overdue' }),
    [QuoteRequestLineItemStatus.Received]: defineMessage({ message: 'Quoted' }),
    [QuoteRequestLineItemStatus.NoBid]: defineMessage({ message: 'No bid' }),
    [QuoteRequestLineItemStatus.Discarded]: defineMessage({ message: 'Discarded' }),
    [QuoteRequestLineItemStatus.Awarded]: defineMessage({ message: 'Awarded' }),
};

export const quoteRequestLineItemAssignmentReasonTranslations: Record<
    QuoteRequestLineItemAssignmentReason,
    MessageDescriptor
> = {
    [QuoteRequestLineItemAssignmentReason.ManuallyAdded]: defineMessage({
        message: 'Manually added',
    }),
    [QuoteRequestLineItemAssignmentReason.ReferenceSupplier]: defineMessage({
        message: 'Reference supplier',
    }),
    [QuoteRequestLineItemAssignmentReason.LineCard]: defineMessage({ message: 'Line card' }),
    [QuoteRequestLineItemAssignmentReason.LineCardRule]: defineMessage({ message: 'Line card rule' }),
    [QuoteRequestLineItemAssignmentReason.ApprovedVendorList]: defineMessage({
        message: 'Approved vendor list',
    }),
};

export const quoteRequestLineItemApprovalStatusTranslations: Record<
    QuoteRequestLineItemApprovalStatus,
    MessageDescriptor
> = {
    [QuoteRequestLineItemApprovalStatus.Approved]: defineMessage({ message: 'Approved' }),
    [QuoteRequestLineItemApprovalStatus.Pending]: defineMessage({ message: 'Pending' }),
    [QuoteRequestLineItemApprovalStatus.Rejected]: defineMessage({ message: 'Rejected' }),
};

export const leadTimeUnitTranslations: Record<LeadTimeUnit, MessageDescriptor> = {
    days: defineMessage({ message: 'Days' }),
    weeks: defineMessage({ message: 'Weeks' }),
    months: defineMessage({ message: 'Months' }),
};

export const sourcingTemplateTagsTranslations: Record<SourcingTemplateTags, MessageDescriptor> = {
    [SourcingTemplateTags.Sourcing]: defineMessage({ message: 'Sourcing' }),
};

export const quoteRequestFieldConfigTranslations: Record<QuoteRequestFieldConfig, MessageDescriptor> = {
    [QuoteRequestFieldConfig.ALWAYS_REQUIRED]: defineMessage({
        message: 'Always required',
    }),
    [QuoteRequestFieldConfig.REQUIRED]: defineMessage({ message: 'Required' }),
    [QuoteRequestFieldConfig.OPTIONAL]: defineMessage({ message: 'Optional' }),
    [QuoteRequestFieldConfig.HIDDEN]: defineMessage({ message: 'Hidden' }),
};

export const quoteRequestFieldCategoryTranslations: Record<QuoteRequestFieldCategory, MessageDescriptor> = {
    [QuoteRequestFieldCategory.QUOTE_REQUEST]: defineMessage({
        message: 'Quote request',
    }),
    [QuoteRequestFieldCategory.OFFER_INFORMATION]: defineMessage({
        message: 'Offer information',
    }),
    [QuoteRequestFieldCategory.PART_INFORMATION]: defineMessage({
        message: 'Part information',
    }),
};

export const partAlternativeSimilarityTranslations: Record<PartAlternativeSimilarityEnum, MessageDescriptor> = {
    [PartAlternativeSimilarityEnum.SuggestedBySupplier]: defineMessage({
        message: 'Suggested by supplier',
    }),
    [PartAlternativeSimilarityEnum.FormFitFunction]: defineMessage({
        message: 'Form Fit Function',
    }),
    [PartAlternativeSimilarityEnum.Functional]: defineMessage({
        message: 'Functional',
    }),
    [PartAlternativeSimilarityEnum.UsedByOthers]: defineMessage({
        message: 'Used by others',
    }),
};

export const ComplianceStatusTranslations: Record<ComplianceStatus, MessageDescriptor> = {
    [ComplianceStatus.Compliant]: defineMessage({ message: 'Compliant' }),
    [ComplianceStatus.CompliantWithExemption]: defineMessage({
        message: 'Compliant with exemption',
    }),
    [ComplianceStatus.NonCompliant]: defineMessage({ message: 'Non-compliant' }),
    [ComplianceStatus.NotRequired]: defineMessage({ message: 'Not required' }),
    [ComplianceStatus.Unknown]: defineMessage({ message: 'Unknown' }),
};

export const ConflictMineralStatusTranslations: Record<ConflictMineralStatus, MessageDescriptor> = {
    [ConflictMineralStatus.Compliant]: defineMessage({ message: 'Compliant' }),
    [ConflictMineralStatus.Unknown]: defineMessage({ message: 'Unknown' }),
};

export const emailStatusTranslations: Record<EmailEventStatus, MessageDescriptor> = {
    Queued: defineMessage({ message: 'Queued' }),
    Bounce: defineMessage({ message: 'Bounce' }),
    Click: defineMessage({ message: 'Click' }),
    Deferred: defineMessage({ message: 'Deferred' }),
    Delivered: defineMessage({ message: 'Delivered' }),
    Drop: defineMessage({ message: 'Drop' }),
    Open: defineMessage({ message: 'Open' }),
    Processed: defineMessage({ message: 'Processed' }),
    SpamReport: defineMessage({ message: 'Spam Report' }),
    Unknown: defineMessage({ message: 'Unknown' }),
    Unsubscribe: defineMessage({ message: 'Unsubscribe' }),
};

export const emailStatusDescriptionTranslations: Record<EmailEventStatus, MessageDescriptor> = {
    Queued: defineMessage({
        message:
            'The email is waiting to be sent. Sometimes due to high load, the email might take a while to be sent.',
    }),
    Bounce: defineMessage({
        message: "The email could not be delivered to the recipient's inbox.",
    }),
    Click: defineMessage({
        message: 'The recipient clicked on a link within the email.',
    }),
    Deferred: defineMessage({
        message: 'The email delivery was delayed but will be retried.',
    }),
    Delivered: defineMessage({
        message: "The email was successfully delivered to the recipient's inbox.",
    }),
    Drop: defineMessage({
        message: 'The email was not sent due to a suppression list or other reasons.',
    }),
    Open: defineMessage({ message: 'The recipient opened the email.' }),
    Processed: defineMessage({
        message: 'The email has been processed and is ready for delivery.',
    }),
    SpamReport: defineMessage({
        message: 'The recipient marked the email as spam.',
    }),
    Unknown: defineMessage({ message: 'The status of the email is unknown.' }),
    Unsubscribe: defineMessage({
        message: 'The recipient unsubscribed from the email list.',
    }),
};

export const abcClassificationLabelTranslations: Record<AbcClassificationLabel, MessageDescriptor> = {
    [AbcClassificationLabel.A]: defineMessage({ message: 'A - High value' }),
    [AbcClassificationLabel.B]: defineMessage({ message: 'B - Medium value' }),
    [AbcClassificationLabel.C]: defineMessage({ message: 'C - Low value' }),
    [AbcClassificationLabel.Unknown]: defineMessage({ message: 'Unknown' }),
};

export const differenceStatusTranslations: Record<DifferenceStatus, MessageDescriptor> = {
    changed: defineMessage({ message: 'Changed' }),
    unchanged: defineMessage({ message: 'Unchanged' }),
    unknown: defineMessage({ message: 'Unknown' }),
};

export const arrayContainsOpTranslations: Record<ArrayContainsOp, MessageDescriptor> = {
    [ArrayContainsOp.IsIn]: defineMessage({ message: 'is in' }),
    [ArrayContainsOp.IsNotIn]: defineMessage({ message: 'is not in' }),
};

export const equalityOpTranslations: Record<EqualityOp, MessageDescriptor> = {
    [EqualityOp.Is]: defineMessage({ message: 'is' }),
    [EqualityOp.IsNot]: defineMessage({ message: 'is not' }),
};

export const partSpecificationTranslations: Record<RuleBasedSelectionPartSpecification, MessageDescriptor> = {
    [RuleBasedSelectionPartSpecification.OffTheShelf]: defineMessage({
        message: 'Off-the-shelf part',
    }),
    [RuleBasedSelectionPartSpecification.Custom]: defineMessage({
        message: 'Custom part',
    }),
};

export const ruleBasedSelectionFallbackTranslations: Record<RuleBasedSelectionFallback, MessageDescriptor> = {
    [RuleBasedSelectionFallback.KeepCurrent]: defineMessage({
        message: 'Keep current selection',
    }),
    [RuleBasedSelectionFallback.SelectNothing]: defineMessage({
        message: 'Select nothing',
    }),
};

export const offerTcoCostTypeTranslations: Record<OfferTcoCostType, MessageDescriptor> = {
    ['ReReeling']: defineMessage({ message: 'Re-reeling costs' }),
};

export const assemblyOriginTranslations: Record<AssemblyDTO['origin']['type'], MessageDescriptor> = {
    Manual: defineMessage({ message: 'Manually created' }),
    ApiImport: defineMessage({ message: 'API import' }),
    BomImport: defineMessage({ message: 'BOM import' }),
};

export const overallRiskShortTranslations: Record<OverallRisk, MessageDescriptor> = {
    [OverallRisk.LowRisk]: defineMessage({ message: 'Low risk' }),
    [OverallRisk.MediumLowRisk]: defineMessage({ message: 'Medium low risk' }),
    [OverallRisk.MediumRisk]: defineMessage({ message: 'Medium risk' }),
    [OverallRisk.MediumHighRisk]: defineMessage({ message: 'Medium high risk' }),
    [OverallRisk.HighRisk]: defineMessage({ message: 'High risk' }),
};
